<script setup>
import { router, Head, Link, useForm } from '@inertiajs/vue3';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetValidationErrors from '@/Jetstream/ValidationErrors.vue';
import JetApplicationHeader from '@/Jetstream/ApplicationHeader.vue';
import JetApplicationFooter from '@/Jetstream/ApplicationFooter.vue';
import JetWelcomePanel from '@/Jetstream/WelcomePanel.vue';
import AppTheming from '@/Jetstream/AppTheming.vue';

const props = defineProps({
    canResetPassword: Boolean,
    status: String,
});

const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const navigationSlugs = JSON.stringify(router.page.props.ziggy.navigationSlugs);
const urlNamePrefix = organisation ? "gi." : "";

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
        urlNamePrefix: urlNamePrefix,
    })).post(route(urlNamePrefix+'login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>

<AppTheming />

    <Head title="Log in" />

    <JetApplicationHeader />

    <div id="content-wrapper">

        <div id="main-content" class="container flex flex-col justify-between w-full pt-5 pb-5 mx-auto md:flex-row">

            <div id="col-intro" class="md:w-2/3 md:pr-10 lg:pr-40">
                <JetWelcomePanel />
            </div>
            <div id="col-form" class="md:w-1/3">

                <div class="p-10 mt-10 card sos-programme-form-card rounded-2xl">
                    <JetValidationErrors class="mb-4" />

                    <div v-if="status" class="mb-4 font-bold">
                        {{ status }}
                    </div>

                    <div v-if="!organisation && programme.gi" class="relative px-4 py-3 text-red-700 bg-red-100 border border-red-400 rounded" role="alert">
                        {{ $t('dashboard.login.no_organisation') }}
                    </div>

                    <form @submit.prevent="submit" class="" v-else>
                        <div>
                            <JetLabel for="email" :value="$t('dashboard.login.email')" />
                            <JetInput
                                id="email"
                                v-model="form.email"
                                type="email"
                                class="block w-full mt-1"
                                required
                                autofocus
                            />
                        </div>

                        <div class="mt-4">
                            <JetLabel for="password" :value="$t('dashboard.login.password')" />
                            <JetInput
                                id="password"
                                v-model="form.password"
                                type="password"
                                class="block w-full mt-1"
                                required
                                autocomplete="current-password"
                            />
                        </div>

                        <div class="block mt-4">
                            <label class="flex items-center">
                                <JetCheckbox v-model:checked="form.remember" name="remember" />
                                <span class="ml-2 text-sm">{{$t('dashboard.login.logged_in')}}</span>
                            </label>
                        </div>

                        <div class="flex items-center justify-end mt-4">
                            <Link v-if="canResetPassword" :href="route(urlNamePrefix+'password.request')" class="text-sm text-gray-600 underline hover:text-gray-900">
                                {{$t('dashboard.login.forgot')}}
                            </Link>

                            <JetButton class="ml-4 whitespace-nowrap" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                                {{$t('dashboard.login.log_in')}}
                            </JetButton>

                        </div>

                        <div class="flex items-center justify-end mt-4">
                            <p class=""><span class="mr-5 text-sm">{{$t('dashboard.login.not_a_member')}}</span>
                             <Link :href="route(urlNamePrefix+'register')" class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase transition bg-gray-500 border border-transparent rounded-md hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25">{{$t('dashboard.login.register')}}</Link>
                            <!-- <a href="/{ programme }/{ organisation }/register" class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white normal-case transition bg-gray-500 border border-transparent rounded-md hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring focus:ring-gray-300 disabled:opacity-25">Register</a> -->

                            </p>

                        </div>
                    </form>

                </div>


            </div>

        </div>

    </div>

    <JetApplicationFooter :programme="programme" :organisation="organisation" />

</template>
